import React from 'react';
import { LuChevronDown, LuChevronUp } from 'react-icons/lu';
import { t } from '../../services/HelperService';
import { AttachmentChip } from './AttachmentChip';
import { KeepAsReferencePopover } from './KeepAsReferencePopover';

/** Chips shown before the strip folds the rest behind "Session files (n)". */
export const STRIP_VISIBLE_CHIPS = 4;

/**
 * The row of chips under the composer: what goes out with this message first (pasted
 * screenshots), then the session's files, newest first. Beyond four the rest fold
 * behind a "Session files (n)" expander. The "keep" bookmark on a chip opens the
 * KeepAsReferencePopover under it.
 *
 * Props:
 * - attachments: chip entries (see AttachmentChip); `withMessage` ones sort first
 * - onRemove(attachment) / onConfirm(attachment) / onOpen(attachment) / onMarkup(attachment)
 * - onKeep(attachment, { key, description }): async; resolves when the file is in the library
 * - disabled
 */
export const AssistantAttachmentStrip = ({ attachments, disabled, onRemove, onKeep, onConfirm, onOpen, onMarkup }) => {
  const list = React.useMemo(() => {
    const all = Array.isArray(attachments) ? attachments.filter(Boolean) : [];
    const withMessage = all.filter((a) => a.withMessage);
    const rest = all.filter((a) => !a.withMessage);
    return [...withMessage, ...rest];
  }, [attachments]);
  const [expanded, setExpanded] = React.useState(false);
  const [keeping, setKeeping] = React.useState(null); // the chip id with the popover open

  React.useEffect(() => {
    if (keeping && !list.some((a) => a.id === keeping)) setKeeping(null);
  }, [keeping, list]);

  if (list.length === 0) return null;

  const folded = !expanded && list.length > STRIP_VISIBLE_CHIPS;
  const shown = folded ? list.slice(0, STRIP_VISIBLE_CHIPS) : list;
  const hidden = list.length - shown.length;
  const canKeep = typeof onKeep === 'function';

  return (
    <div data-tagname="attachmentStrip" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
      {shown.map((a) => (
        <div key={a.id} style={{ position: 'relative' }}>
          <AttachmentChip
            attachment={a}
            disabled={disabled}
            onRemove={onRemove}
            onConfirm={onConfirm}
            onOpen={onOpen}
            onMarkup={onMarkup}
            onKeep={canKeep ? () => setKeeping(a.id) : undefined}
          />
          {keeping === a.id && (
            <KeepAsReferencePopover
              attachment={a}
              onClose={() => setKeeping(null)}
              onKeep={async (fields) => {
                await onKeep(a, fields);
                setKeeping(null);
              }}
            />
          )}
        </div>
      ))}
      {list.length > STRIP_VISIBLE_CHIPS && (
        <button
          type="button"
          data-tagname="attachmentStripToggle"
          onClick={() => setExpanded((v) => !v)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 3, border: 'none', background: 'none', color: '#6f42c1', fontSize: 11, fontWeight: 600, padding: '0 4px', cursor: 'pointer' }}
        >
          {folded ? t('Session files ({{count}})', { count: list.length }) : t('Show fewer')}
          {folded ? <LuChevronDown size={12} /> : <LuChevronUp size={12} />}
          {folded && hidden > 0 && <span style={{ color: '#64748b', fontWeight: 400 }}>{t('+{{count}} more', { count: hidden })}</span>}
        </button>
      )}
    </div>
  );
};
